import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  MutableRefObject,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory, useParams } from "react-router";

import { SiteSearch } from "components";
import { IPager, ISearchSubmitData } from "types";
import { urlService } from "services";

import {
  searchMetaSelector,
  doSearch,
  searchDataSelector,
  clearSearchState,
} from "../state";
import { useMenuOverlayClose } from "../utils";

interface SiteSearchContainerProps {
  redirectPath: string;
}

export const SiteSearchContainer: React.FC<SiteSearchContainerProps> = ({
  redirectPath,
}) => {
  const {
    status,
    error,
    pagination,
    query: currentQuery,
    hasSearched,
  } = useSelector(searchMetaSelector);
  const results = useSelector(searchDataSelector);
  const dispatch = useDispatch();
  const searchContainer = useRef() as MutableRefObject<HTMLDivElement>;

  const history = useHistory();
  let { query: initQuery, page: initPage } = useParams<{
    query: string;
    page: string;
  }>();

  const { menuOverlayOpen } = useMenuOverlayClose(!!initQuery && !!initPage);

  const dispatchSearch = useCallback(
    (query: string, page: number) => {
      dispatch(doSearch({ page, query }));
    },
    [dispatch]
  );

  /*
   * Get search term and page from URL params,
   * if there is one, open menu and search
   * (!) and perfrom this effect ONCE
   * */
  const initSearchPerformed = useRef(false);
  useEffect(() => {
    if (initSearchPerformed.current) return;

    initSearchPerformed.current = true;

    if (!initQuery || !initPage) return;

    dispatchSearch(initQuery, +initPage);
  }, [dispatchSearch, initPage, initQuery]);

  const handleSearch = (query: string, page: number) => {
    if (!query.length) {
      dispatch(clearSearchState());
      return;
    }

    // perform search
    dispatchSearch(query, page);

    // update search query AND page in URL
    history.replace(urlService.generateSearchUrl(query, page));
  };

  const handlePageChange = useCallback(
    (page: number) => {
      // if there's no query in state, don't perform page change
      if (!currentQuery) return;

      // scroll to top of search container for UX
      if (searchContainer?.current) {
        searchContainer.current.scrollIntoView();
      }

      // perform search
      dispatchSearch(currentQuery, page);

      // update page in URL
      history.replace(urlService.generateSearchUrl(currentQuery, page));
    },
    [currentQuery, dispatchSearch, searchContainer, history]
  );

  const pager = useMemo((): IPager => {
    return {
      currentPage: pagination?.page || 1,
      totalPages: Math.ceil(
        (pagination?.total || 0) / (pagination?.amount || 1)
      ),
      onPageChange: handlePageChange,
    };
  }, [pagination, handlePageChange]);

  // Clear search if overlay is closed
  useEffect(() => {
    if (!menuOverlayOpen) {
      // push back the original URL where the search was opened
      history.replace(redirectPath);

      // clear search
      dispatch(clearSearchState());
    }
  }, [dispatch, menuOverlayOpen, history, redirectPath]);

  return (
    <div className="site-search-container" ref={searchContainer}>
      <SiteSearch
        initSearchValue={initQuery}
        onSearch={(data: ISearchSubmitData) => handleSearch(data.search, 1)}
        status={status}
        error={error}
        results={results}
        pager={pager}
        total={pagination?.total}
        hasSearched={hasSearched}
        menuOverlayOpen={menuOverlayOpen}
      />
    </div>
  );
};
